/**
 * ForgotPinDialog.jsx
 *
 * Modal triggered from the "Forgot your PIN?" link on the lock overlay.
 * Sends a PIN reset ticket to IT via the Functions API (app-only mail).
 * IT can reply directly to the user's email (replyTo on the outbound message).
 */

import React, { useState } from 'react';
import { useAuth } from '../auth/AuthContext.jsx';
import { sendPinResetRequest } from '../api/itSupport.js';

const IT_SUPPORT = 'itsupport@therapieclinic.com';

export default function ForgotPinDialog({ onClose }) {
  const { user } = useAuth();
  const [note, setNote]     = useState('');
  const [busy, setBusy]     = useState(false);
  const [error, setError]   = useState(null);
  const [sent, setSent]     = useState(false);

  async function submit(e) {
    e?.preventDefault();
    if (busy || sent) return;
    setBusy(true);
    setError(null);
    try {
      await sendPinResetRequest(user, note.trim());
      setSent(true);
    } catch (e) {
      setError(e.message || 'Could not send the email.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="forgotpin-title"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 9500,
        background: 'rgba(15, 23, 42, 0.55)',
        backdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}
    >
      <form
        onSubmit={submit}
        style={{
          width: '100%', maxWidth: 420,
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 14, padding: 24,
          boxShadow: '0 20px 50px rgba(0,0,0,0.25)',
        }}
      >
        <h2 id="forgotpin-title" style={{ fontSize: 17, fontWeight: 600, margin: 0 }}>
          {sent ? 'Request sent' : 'Open a ticket with IT'}
        </h2>
        <p style={{ fontSize: 13, color: 'var(--text-3)', margin: '6px 0 18px', lineHeight: 1.5 }}>
          {sent
            ? <>Request sent to <strong>{IT_SUPPORT}</strong> (you're CC'd at <strong>{user?.email}</strong>). When IT hits <strong>Reply</strong>, it goes to you — not the AE Tracker mailbox.</>
            : <>We'll email <strong>{IT_SUPPORT}</strong> and CC you at <strong>{user?.email}</strong>. <strong>Reply</strong> in Outlook is set to your address so IT's response lands in your inbox.</>
          }
        </p>

        {!sent && (
          <>
            <label style={{ display: 'block', marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-3)', marginBottom: 4 }}>
                Add a note (optional)
              </div>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                disabled={busy}
                rows={3}
                placeholder="e.g. I'm at reception and need to access a case before 4pm"
                maxLength={500}
                style={{
                  width: '100%', padding: '10px 12px', fontSize: 13,
                  border: '1px solid var(--border-strong)', borderRadius: 8,
                  background: 'var(--surface)', fontFamily: 'inherit', resize: 'vertical',
                  lineHeight: 1.5,
                }}
              />
            </label>

            <div style={{
              padding: '10px 12px', background: 'var(--surface-2)',
              border: '1px solid var(--border)', borderRadius: 8,
              fontSize: 12, color: 'var(--text-3)', lineHeight: 1.55,
            }}>
              We'll include your name, email, role, and clinic in the ticket so IT can verify you.
            </div>
          </>
        )}

        {error && (
          <div className="callout danger" style={{ marginTop: 14, fontSize: 12, padding: '8px 10px' }}>
            <div style={{ flex: 1 }}>{error}</div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="btn ghost"
            style={{ flex: 1, justifyContent: 'center', gap: 0 }}
          >
            {sent ? 'Close' : 'Cancel'}
          </button>
          {!sent && (
            <button
              type="submit"
              disabled={busy}
              className="btn primary"
              style={{ flex: 1, justifyContent: 'center', gap: 0 }}
            >
              {busy ? 'Sending…' : 'Send request'}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
